import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsContent } from '@icms/core/content';
import { Product } from '../../../product.decorator';
import { NProductViewCalculatorModal } from '../../calculator/modal/product-view-calculator-modal.component';
import { NProductViewImageSizesModal } from '../../image-sizes-modal/product-view-image-sizes-modal.component';
import { NProductViewLayout } from '../../layout/product-view-layout.namespace';
import { NProductViewPriceListModal } from '../../price-list-modal/product-view-price-list-modal.component';
import { IProductDataV3 } from '@df/catalog/product/product';

export namespace NProductViewDetailsBottomList {
    export interface IItem {
        id: string;
        icon?: string;
        image?: string;
        rightIcon?: string;
        title: string;
        subtitle?: string;
        link?: string;
        table?: string;
        onClick?: () => void;
    }
}

@Component({
    standalone: true,
    imports: [ICmsClientModule, DfImageDirective, HrefComponent, RouterModule, TranslationModule, CommonModule],
    selector: 'product-view-details-bottom-list',
    templateUrl: './product-view-details-bottom-list.html'
})
export class ProductViewDetailsBottomListComponent extends UiComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    @ICmsContent({
        watchDepth: 2
    })
    content?: NProductViewLayout.IBlockDataContent;

    @Input({
        required: true
    })
    product!: Product<IProductDataV3>;

    items: NProductViewDetailsBottomList.IItem[] = [];

    private get parent(): Product {
        return this.product.parent as Product;
    }

    private dfModalService = inject(DfModalService);

    override ngOnChanges(changes: any): void {
        this.updateItems();
    }

    protected openCalculatorModal(): void {
        this.dfModalService.open(undefined, NProductViewCalculatorModal.BLOCK_ID, undefined, this.parent.id);
    }

    protected openPriceListModal(): void {
        this.dfModalService.open(
            undefined,
            NProductViewPriceListModal.BLOCK_ID,
            'product-view-price-list-modal-' + this.parent.id.toString(),
            {
                table: this.product?.getAttr('pricing_modal')
            }
        );
    }

    protected openImageSizesModal(): void {
        this.dfModalService.open(undefined, NProductViewImageSizesModal.BLOCK_ID, this.parent.id.toString());
    }

    protected get isSM(): boolean {
        return this.client.sizeId < 2;
    }

    private get returnpolicy(): string | undefined {
        return this.product?.getAttr('returnpolicy');
    }

    private get displayGetSample(): boolean {
        return ((this.product?.isComplexPanel || this.product?.isStandard) && this.product?.data?.swatch_request) || false;
    }

    private get displayPriceList(): boolean {
        return this.product.displayPriceList;
    }

    private get displayRoomCalculator(): boolean {
        const nrcExist = !!this.parent.children.find(child => (child as Product<IProductDataV3>).data?.nrc_calculator);
        return this.product.displayRoomCalculator && nrcExist;
    }

    private get displayImagesSizes(): boolean {
        return this.product.displayImagesSizes;
    }

    private updateItems(): void {
        if (this.content && this.product) {
            const items: NProductViewDetailsBottomList.IItem[] = [];
            const {
                priceListDescription,
                getSampleSubtitle,
                getSampleTitle,
                getSampleUrl,
                talkToExpertImage,
                talkToExpertDescription,
                talkToExpertTitle,
                talkToExpertUrl
            } = this.content;
            if (this.displayImagesSizes) {
                items.push({
                    id: 'imageSizes',
                    icon: 'icon-image',
                    title: 'What size images should I upload?',
                    subtitle: 'Click here for some tips!',
                    rightIcon: 'icon-arrow-right',
                    onClick: () => {
                        this.openImageSizesModal();
                    }
                });
            }
            if (this.displayPriceList) {
                items.push({
                    id: 'priceList',
                    icon: 'icon-money',
                    title: 'Price List',
                    subtitle: priceListDescription,
                    rightIcon: 'icon-arrow-right',
                    onClick: () => {
                        this.openPriceListModal();
                    }
                });
            }
            if (this.displayRoomCalculator) {
                items.push({
                    id: 'calcu',
                    icon: 'icon-calc',
                    title: 'Room Acoustics Calculator',
                    subtitle: 'Find the right dimensions for your room',
                    rightIcon: 'icon-arrow-right',
                    onClick: () => {
                        this.openCalculatorModal();
                    }
                });
            }
            if (this.displayGetSample && getSampleUrl && getSampleTitle) {
                items.push({
                    id: 'sample',
                    icon: 'icon-sample',
                    title: getSampleTitle,
                    subtitle: getSampleSubtitle,
                    link: getSampleUrl
                });
            }
            if (talkToExpertDescription && talkToExpertUrl && talkToExpertImage && talkToExpertTitle) {
                items.push({
                    id: 'expert',
                    image: talkToExpertImage,
                    title: talkToExpertTitle,
                    subtitle: talkToExpertDescription,
                    link: talkToExpertUrl
                });
            }
            if (this.returnpolicy) {
                items.push({
                    id: 'returns',
                    icon: 'icon-diffuse',
                    title: 'Returns',
                    subtitle: this.returnpolicy
                });
            }
            this.items = items;
        } else {
            this.items = [];
        }
        this.detectChanges();
    }
}
